import { fetchUserCheckInOut } from "../../services/actions/main/userCheckInOutActions";
import { addPatientNoteReply } from "../../services/actions/pharmacy/other";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import * as routeNames from '../../routes/routeName';
import {
	APP_TIMEZONE,
	formatDateToYYYYMMDD_CA,
	getCurrentDateInAppTimezone,
	getCurrentTimeInAppTimezone,
	shiftAppDate
} from '../constants/formInitialStates';

/**
 * Queue toast calls so react-toastify only processes one default-container update at a time.
 * This avoids the intermittent "Cannot set properties of undefined (setting 'toggle')" crash
 * that can happen when multiple toasts are fired in the same render/update window.
 */
const toastQueue = [];
let queueTimer = null;
const PROCESS_INTERVAL_MS = 80;

function processToastQueue() {
	if (toastQueue.length === 0) {
		queueTimer = null;
		return;
	}
	const { type, msg, opts } = toastQueue.shift();
	try {
		if (type === 'success') toast.success(msg, opts);
		else if (type === 'error') toast.error(msg, opts);
		else if (type === 'info') toast.info(msg, opts);
		else if (type === 'warning') toast.warning(msg, opts);
	} catch (_) { /* no-op: never let toast crash the app */ }
	queueTimer = setTimeout(processToastQueue, PROCESS_INTERVAL_MS);
}

function scheduleToast(type, msg, opts = {}) {
	toastQueue.push({ type, msg, opts });
	if (queueTimer == null) {
		// Defer first call to next tick so we're never in the middle of React render
		queueTimer = setTimeout(() => processToastQueue(), 0);
	}
}

/** Safe toast wrapper: queued, deferred, try/catch. Use this everywhere to avoid toast-related crashes. */
const safeToast = {
	success: (msg, opts) => scheduleToast('success', msg, opts),
	error: (msg, opts) => scheduleToast('error', msg, opts),
	info: (msg, opts) => scheduleToast('info', msg, opts),
	warning: (msg, opts) => scheduleToast('warning', msg, opts),
};
export { safeToast };

const formatUtcCalendarDate = (year, month, day, options, locale = 'en-US') =>
	new Intl.DateTimeFormat(locale, { timeZone: 'Canada/Pacific', ...options }).format(
		new Date(Date.UTC(year, month - 1, day))
	);

const getAppDateParts = (value) => {
	const formattedDate = formatDateToYYYYMMDD_CA(value);
	if (!formattedDate) return null;

	const [year, month, day] = formattedDate.split('-').map(Number);
	if ([year, month, day].some(Number.isNaN)) return null;

	return {
		year,
		month,
		day,
		formattedDate,
	};
};

const formatInAppTimezone = (value, options, locale = 'en-US') => {
	const date = value instanceof Date ? value : new Date(value);
	if (Number.isNaN(date.getTime())) return '';

	return new Intl.DateTimeFormat(locale, {
		timeZone: APP_TIMEZONE,
		...options,
	}).format(date);
};

export const getTimeSchedule = (startDateValue, endDateValue, setMessage, dispatch) => {
	if (!startDateValue) {
		setMessage('Please select the starting date.');
	} else if (!endDateValue) {
		setMessage('Please select the ending date.');
	} else if (startDateValue === endDateValue) {
		setMessage('Starting and ending date should not be the same.');
	} else if (new Date(endDateValue) < new Date(startDateValue)) {
		setMessage('Ending date should be greater than the starting date.');
	} else {
		const startDate = new Date(startDateValue);
		const endDate = new Date(endDateValue);
		const timeDiff = Math.abs(endDate - startDate);
		const diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24));

		if (diffDays > 30) {
			setMessage('The gap between the starting and ending date should not exceed 30 days.');
		} else {
			setMessage('');
			dispatch(fetchUserCheckInOut(startDateValue, endDateValue));
		}
	}
};

export const displayScheduleForMonth = (startDateValue, endDateValue, setStartMonth, setEndMonth) => {
	const startDateParts = getAppDateParts(startDateValue);
	const endDateParts = getAppDateParts(endDateValue);

	if (!startDateParts) {
		setStartMonth('');
		setEndMonth('');
		return;
	}

	const firstDayOfMonth = formatUtcCalendarDate(
		startDateParts.year,
		startDateParts.month,
		1,
		{ month: 'long' }
	);
	const lastDayOfMonth = endDateParts
		? formatUtcCalendarDate(
			endDateParts.year,
			endDateParts.month,
			1,
			{ month: 'long', year: 'numeric' }
		)
		: '';

	if (firstDayOfMonth === lastDayOfMonth) {
		setStartMonth(firstDayOfMonth);
		setEndMonth('');
	} else if (!lastDayOfMonth) {
		setEndMonth('');
		setStartMonth(firstDayOfMonth)
	} else if (firstDayOfMonth !== lastDayOfMonth) {
		setStartMonth(firstDayOfMonth)
		setEndMonth(lastDayOfMonth)
	}
}


export const formatTime = (dateTime) => {
	if (dateTime) {
		const formattedTime = formatInAppTimezone(dateTime, {
			hour: '2-digit',
			minute: '2-digit',
			hour12: true,
		});
		return formattedTime;
	}
	return '';
};


export function calculateTotalHours(startDateValue, endDateValue) {
	if (startDateValue && endDateValue) {
		const startDate = new Date(startDateValue);
		const endDate = new Date(endDateValue);

		if (!isNaN(startDate) && !isNaN(endDate)) {
			const timeDiff = endDate - startDate;

			if (timeDiff >= 0) {
				const daysDiff = Math.ceil(timeDiff / (24 * 60 * 60 * 1000));
				const totalHours = (daysDiff + 1) * 8.00;

				if (totalHours % 1 === 0) {
					return totalHours.toFixed(2);
				} else {
					return totalHours.toFixed(2);
				}
			}
		}
	}

	return '';
}



export const formatDate = (inputDate) => {
	const dateParts = getAppDateParts(inputDate);
	if (!dateParts) return '';

	const monthAndYear = formatUtcCalendarDate(
		dateParts.year,
		dateParts.month,
		1,
		{ month: 'short', year: 'numeric' }
	);
	return `${String(dateParts.day).padStart(2, '0')} ${monthAndYear}`;
};

/**
 * Format date time in Canada/Pacific timezone
 * Format: "Jan 31, 2026 12:11 AM"
 * @param {string} dateString - Date string to format
 * @returns {string} Formatted date time string
 */
export const formatDateTimeCanada = (dateString) => {
	if (!dateString) return '';
	
	try {
		const date = new Date(dateString);
		
		// Check if date is valid
		if (isNaN(date.getTime())) {
			return dateString; // Return original if invalid
		}
		
		// Format using Canada/Pacific timezone
		const formatter = new Intl.DateTimeFormat('en-US', {
			timeZone: APP_TIMEZONE,
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: 'numeric',
			minute: '2-digit',
			hour12: true
		});
		
		const parts = formatter.formatToParts(date);
		
		// Extract parts
		const month = parts.find(part => part.type === 'month').value;
		const day = parts.find(part => part.type === 'day').value;
		const year = parts.find(part => part.type === 'year').value;
		const hour = parts.find(part => part.type === 'hour').value;
		const minute = parts.find(part => part.type === 'minute').value;
		const dayPeriod = parts.find(part => part.type === 'dayPeriod')?.value || '';
		
		// Format: "Jan 31, 2026 12:11 AM"
		return `${month} ${day}, ${year} ${hour}:${minute} ${dayPeriod.toUpperCase()}`;
	} catch (error) {
		console.error('Error formatting date:', error);
		return dateString; // Return original on error
	}
};

export const dilaudidDeliveryDate = (inputDate) => {
	const dateParts = getAppDateParts(inputDate);
	if (!dateParts) return '';

	const month = formatUtcCalendarDate(dateParts.year, dateParts.month, 1, { month: 'short' });
	return `${month} ${String(dateParts.day).padStart(2, '0')}`;
};

export const chatUserListLoader = Array.from({ length: 6 }, (_, index) => index);

export const patientNotificationListLoader = Array.from({ length: 10 }, (_, index) => index);

export const currentTime = getCurrentTimeInAppTimezone()

export const getCurrentTimeCanada = () => {
	return getCurrentTimeInAppTimezone();
}

export const commentListLoader = Array.from({ length: 2 }, (_, index) => index);

export const wallListLoader = Array.from({ length: 10 }, (_, index) => index);


export const formatMessageTime = (timestamp) => {
	const messageDateParts = getAppDateParts(timestamp);
	if (!messageDateParts) return '';

	const currentDate = getCurrentDateInAppTimezone();
	const yesterdayDate = shiftAppDate(currentDate, -1);

	if (messageDateParts.formattedDate === currentDate) {
		return formatInAppTimezone(timestamp, {
			hour: 'numeric',
			minute: 'numeric',
			hour12: true,
		});
	} else if (messageDateParts.formattedDate === yesterdayDate) {
		return 'Yesterday';
	} else {
		return `${messageDateParts.day}/${messageDateParts.month}/${messageDateParts.year}`;
	}
};


export const PreviewPdf = (response) => {
	const base64Pdf = response
	const binaryData = atob(base64Pdf);
	const arrayBuffer = new ArrayBuffer(binaryData.length);
	const uint8Array = new Uint8Array(arrayBuffer);

	for (let i = 0; i < binaryData.length; i++) {
		uint8Array[i] = binaryData.charCodeAt(i);
	}
	const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
	const pdfUrl = URL.createObjectURL(blob);
	window.open(pdfUrl, '_blank')
}


export const PreviewAndDownloadPdf = (base64Pdf, fileName = 'reports.pdf') => {

	// Convert base64 to binary
	const binaryData = atob(base64Pdf);
	const arrayBuffer = new ArrayBuffer(binaryData.length);
	const uint8Array = new Uint8Array(arrayBuffer);

	for (let i = 0; i < binaryData.length; i++) {
		uint8Array[i] = binaryData.charCodeAt(i);
	}

	const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
	const blobUrl = URL.createObjectURL(blob);

	// Step 1: Trigger file download
	const a = document.createElement('a');
	a.href = blobUrl;
	a.download = fileName;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);

	// // Step 2: Open the same Blob in new tab for preview
	// setTimeout(() => {
	// 	window.open(blobUrl, '_blank');
	// }, 500); // Give some time for the download
};

export const PreviewFileNamePdf = (response, fileName) => {
	const base64Pdf = response;
	const binaryData = atob(base64Pdf);
	const arrayBuffer = new ArrayBuffer(binaryData.length);
	const uint8Array = new Uint8Array(arrayBuffer);

	for (let i = 0; i < binaryData.length; i++) {
		uint8Array[i] = binaryData.charCodeAt(i);
	}

	const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
	const pdfUrl = URL.createObjectURL(blob);

	// Build a new HTML document for preview with download
	const html = `
	  <html>
		<head><title>${fileName}</title></head>
		<body style="margin:0">
		  <embed src="${pdfUrl}" type="application/pdf" width="100%" height="100%" />
		</body>
	  </html>
	`;

	const previewWindow = window.open();
	previewWindow.document.open();
	previewWindow.document.write(html);
	previewWindow.document.close();
};


// export const PreviewFileNamePdf = (response, fileName) => {
// 	const base64Pdf = response;
// 	const binaryData = atob(base64Pdf);
// 	const arrayBuffer = new ArrayBuffer(binaryData.length);
// 	const uint8Array = new Uint8Array(arrayBuffer);

// 	for (let i = 0; i < binaryData.length; i++) {
// 		uint8Array[i] = binaryData.charCodeAt(i);
// 	}
// 	const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
// 	const pdfUrl = URL.createObjectURL(blob);
// 	const newWindow = window.open(pdfUrl, '_blank');
// 	if (newWindow && fileName) {
// 		newWindow.location.href = `/${fileName}`;
// 	}
// 	// const link = document.createElement('a');
// 	// link.href = pdfUrl;
// 	// link.download = fileName; // This sets the filename
// 	// link.click();

// 	// // Clean up
// 	// setTimeout(() => {
// 	// 	URL.revokeObjectURL(pdfUrl);
// 	// }, 1000);
// };



export const removeWorkSpaceTab = () => {
	sessionStorage.removeItem('work_tab')
}

export const handalPatientNotesreply = (value, addTeamNoteData, setAddTeamNoteData, dispatch, useInfiniteScroll = false) => {
	if (value && addTeamNoteData.addReply) {
		dispatch(addPatientNoteReply(value.note_id,
			addTeamNoteData.addReply,
			addTeamNoteData.pageNumber, addTeamNoteData, setAddTeamNoteData, null, useInfiniteScroll
		))
	}
}

export const PharmacyformattedDate = (dateString) => {
	if (!dateString) return ''
	const dateParts = getAppDateParts(dateString);
	if (!dateParts) return '';

	const month = formatUtcCalendarDate(dateParts.year, dateParts.month, 1, { month: 'short' });
	return `${String(dateParts.day).padStart(2, '0')} ${month}, ${dateParts.year}`;
};

export const calculateDaysBetween = (start, end) => {
	const startDate = new window.Date(start);
	const endDate = new window.Date(end);
	const timeDiff = endDate.getTime() - startDate.getTime();
	return Math.ceil(timeDiff / (1000 * 3600 * 24));
};


export const DownLoadPDF = (response, file_Name) => {
	const base64Pdf = response;
	const binaryData = atob(base64Pdf);
	const arrayBuffer = new ArrayBuffer(binaryData.length);
	const uint8Array = new Uint8Array(arrayBuffer);

	for (let i = 0; i < binaryData.length; i++) {
		uint8Array[i] = binaryData.charCodeAt(i);
	}

	const blob = new Blob([arrayBuffer], { type: 'application/pdf' });

	// Generate a filename dynamically
	// const currentDate = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
	const fileName = `${file_Name}.pdf`;

	// Initiate download
	const link = document.createElement('a');
	link.href = URL.createObjectURL(blob);
	link.download = fileName;
	link.click();

	// Clean up the object URL
	URL.revokeObjectURL(link.href);
};

export const DownLoadExcel = (base64Excel, fileName = 'download.xlsx') => {
	const binaryData = atob(base64Excel);
	const arrayBuffer = new ArrayBuffer(binaryData.length);
	const uint8Array = new Uint8Array(arrayBuffer);

	for (let i = 0; i < binaryData.length; i++) {
		uint8Array[i] = binaryData.charCodeAt(i);
	}

	const blob = new Blob([arrayBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

	// Initiate download
	const link = document.createElement('a');
	link.href = URL.createObjectURL(blob);
	link.download = fileName;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);

	// Clean up the object URL
	URL.revokeObjectURL(link.href);
};


export const showToast = (message, type = "success") => {
	try {
		const options = {
			autoClose: 2000,
			closeOnClick: true,
			hideProgressBar: false,
			style: {
				color: "#333",
				boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
				borderRadius: "8px",
				padding: "16px",
				maxWidth: "320px",
			},
		};
		if (type === "success") safeToast.success(message, options);
		else if (type === "error") safeToast.error(message, options);
		else if (type === "info") safeToast.info(message, options);
		else if (type === "warning") safeToast.warning(message, options);
	} catch (_) {
		// Prevent react-toastify errors from crashing the app
	}
};


export const NonFormattedDate = (dateString) => {
	const [day, month, year] = dateString.split(' ');
	const monthIndex = new Date(Date.parse(month + " 1, 2021")).getMonth() + 1; // Get month index (1-12)
	return `${year}-${String(monthIndex).padStart(2, '0')}-${String(day).padStart(2, '0')}`; // Format to YYYY-MM-DD
};

export const AccountabilityActions = ({ prepCount, finalCount, finalCheckAt, userType, navigate }) => {

	if (!prepCount && !finalCount) return null;

	return (
		<div className="flex justify-between gap-4 mb-1">
			{prepCount > 0 && (
				<div
					className={`flex flex-col items-start ${finalCheckAt === 'preparation' &&
						finalCount > 0 &&
						prepCount > 0 &&
						userType === 'pharmacist'
						? 'border-r border-white'
						: ''
						} pr-4 w-1/2 pt-1`}
				>
					<span className="text-sm font-semibold underline cursor-pointer"
						onClick={() => navigate(`${routeNames.pharmacy_accountability_today_missed_rx}?type=prep_missed`)}>Prep Check</span>
					<span className="text-xs">{prepCount} pending</span>
					{/* <div className="flex items-center gap-2">

						<button
							onClick={() => navigate(`${routeNames.pharmacy_accountability_today_missed_rx}?type=prep_missed`)}
							className="w-fit bg-[#ffcc00] whitespace-nowrap hover:bg-yellow-500 text-black font-bold py-1 px-3 text-xs rounded"
						>
							Take Action
							{prepCount} Pending
						</button>
					</div> */}
				</div>
			)}
			{finalCount > 0 && finalCheckAt === 'preparation' && userType === 'pharmacist' && (
				<div
					className={`flex flex-col pt-1 ${prepCount > 0 ? 'items-end w-1/2' : ''}`}>
					<span className="text-sm font-semibold underline cursor-pointer"
						onClick={() => navigate(`${routeNames.pharmacy_accountability_today_missed_rx}?type=final_missed`)}>Final Check</span>
					<span className="text-xs">{finalCount} pending</span>
					{/* <button
						onClick={() => navigate(`${routeNames.pharmacy_accountability_today_missed_rx}?type=final_missed`)}
						className="w-fit bg-[#ffcc00] whitespace-nowrap hover:bg-yellow-500 text-black font-bold py-1 px-3 text-xs rounded"
					>
						Take Action
						{finalCount} Pending
					</button> */}
				</div>
			)}
		</div>

	);
};




export const TodayMissedActions = ({ prepCount, finalCount, finalCheckAt, userType, accountSettings }) => {


	if (!prepCount && !finalCount) return null;

	return (

		<div className="flex justify-between items-center w-full border-b-1 text-white rounded-t-sm h-full ">
			{prepCount > 0 && (
				<div
					className={`flex flex-col basis-1/2 items-center ${finalCheckAt === 'preparation' &&
						finalCount > 0 &&
						prepCount > 0 &&
						userType === 'pharmacist'
						? 'border-r border-white'
						: ''
						} `}
				>
					<span className="text-sm leading-[11px] font-semibold">Prep Check</span>
					<span className="text-xs">{prepCount} pending</span>
				</div>
			)}
			{finalCount > 0 && accountSettings === 'preparation' && userType === 'pharmacist' && (
				<div
					className={`flex flex-col items-center basis-1/2 ${prepCount > 0 ? 'items-end ' : ''
						}`}
				>
					<span className="text-sm  leading-[11px] font-semibold">Final Check</span>
					<span className="text-xs">{finalCount} pending</span>
				</div>
			)}

		</div>

	);
};

export const handleNavigateToInvestigateRx = (rxNumber, loginUserType) => {
	// if (loginUserType === 'pharmacist') {
		window.open(`/pharmacy-investigate-rx?rx-number=${rxNumber}`, '_blank')
	// }
}



export const GetPatientProfile = ({ phn, id, last_name, first_name }) => {
	return (
		<Link to={`${routeNames.PHARMACY_PATIENT_PROFILE}?phn=${phn}&id=${id}`} target='_blank'
			className='cursor-pointer' title='View Profile'>
			{last_name} {first_name}
		</Link>
	)
}

/**
 * Capitalizes the first letter of each word in a string
 * @param {string} str - The string to capitalize
 * @returns {string} - The string with first letter of each word capitalized
 */
export const capitalizeWords = (str) => {
	if (!str) return '';
	return str
		.split(' ')
		.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(' ');
};


